/**
 * @file Turns an operator's pasted text into staged fetch entries. Consumer:
 * `SignifyFetchJobPasteInput`, both for the live Add-button preview and for the add itself.
 * Pure and order-preserving: dedup and the 1000-input cap belong to the draft, not here.
 * Two rules rest on assumptions nobody has confirmed, each isolated in one named function so that
 * dropping it is one deletion: `splitLineIntoFields` (commercial codes carry internal spaces) and
 * `readTwoColumnQuantity` (BOMs arrive as a `code`/`qty` spreadsheet pair).
 */

import type { SignifyFetchJobDraftEntry } from '../../index.types';

const LINE_SEPARATOR = /\r?\n/;
const FIELD_SEPARATOR = /[\t,;]/;
const QUANTITY_FIELD = /^\d{1,5}$/;
const NUMERIC_GROUP_ROW = /^\d{6,}( \d{6,})+$/;

/**
 * Splits one line into fields on tab, comma or semicolon, and never on a bare space.
 * ASSUMPTION, unverified against the live catalogue: Signify commercial codes carry internal spaces.
 * A shattered commercial code searches wrong silently, whereas an unsplit line is one visibly wrong
 * row the operator can fix. If a catalogue sample shows codes never contain spaces, this becomes a
 * split on whitespace and `expandNumericGroupRow` goes with it.
 */
const splitLineIntoFields = (line: string): string[] =>
  line
    .split(FIELD_SEPARATOR)
    .map((field) => field.trim())
    .filter((field) => field.length > 0);

/**
 * Reads `code<TAB>qty`: exactly two fields whose second is one to five ASCII digits.
 * HEURISTIC, unconfirmed with an operator: it assumes BOMs arrive as two spreadsheet columns. The
 * digit bound is what stops a 6NC, UPC or 12NC being read as a quantity, and a `0` becomes `1`.
 * Delete this function and quantity is a table-only edit.
 */
const readTwoColumnQuantity = (fields: string[]): SignifyFetchJobDraftEntry | null => {
  if (fields.length !== 2 || !QUANTITY_FIELD.test(fields[1])) return null;
  return { input: fields[0], quantity: Math.max(1, Number(fields[1])) };
};

/**
 * Expands a field of space-separated digit groups of six or more digits into one entry per group —
 * a row of 12NCs copied horizontally out of a spreadsheet. It rests on the same assumption as
 * `splitLineIntoFields`: a space inside a field is data unless the whole field is such a row.
 */
const expandNumericGroupRow = (field: string): string[] =>
  NUMERIC_GROUP_ROW.test(field) ? field.split(' ') : [field];

export function parsePastedSignifyCodes(text: string): SignifyFetchJobDraftEntry[] {
  return text
    .split(LINE_SEPARATOR)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .flatMap((line) => {
      const fields = splitLineIntoFields(line);
      const twoColumn = readTwoColumnQuantity(fields);
      if (twoColumn) return [twoColumn];
      return fields.flatMap((field) =>
        expandNumericGroupRow(field).map((input) => ({ input, quantity: 1 })),
      );
    });
}
